"use client";

import { usePathname } from "next/navigation";
import type { JSX } from "react";
import { useCallback, useEffect, useRef } from "react";
import { Button, Icon, Image, Link, Text, View } from "@/components";
import type { AsideProps } from "@/components/types";
import useWindowSize from "@/hooks/useWindowSize";
import { useLayout } from "@/providers";

/**
 * @name SidebarHovered
 * @description Renders a sidebar that expands on hover. Updated to support flat navigation data.
 */
const SidebarHovered = ({
  data,
  setSidebar,
  sidebarIsOpen
}: AsideProps): JSX.Element => {
  const path = usePathname();
  const { t } = useLayout();
  const { windowSize } = useWindowSize();
  const sidebarRef = useRef<HTMLDivElement | null>(null);

  /**
   * Handles clicks outside the sidebar to close it on smaller screens.
   */
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      const currentWindowSize = window.innerWidth;

      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        if (currentWindowSize < 1400) {
          setSidebar(false);
        }
      }
    },
    [setSidebar]
  );

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  return (
    <View
      type="aside"
      ref={sidebarRef}
      className={`xxl:w-24 group max-xxl:w-[320px] hover:xxl:w-[320px] z-[90] ${
        sidebarIsOpen
          ? "translate-x-0 visible opacity-100"
          : "ltr:-translate-x-full rtl:translate-x-full invisible opacity-0"
      } fixed ltr:left-0 rtl:right-0 shadow-lg h-full bg-white dark:bg-gray-900 border-r border-gray-100 dark:border-gray-800 top-0 transition-[transform,opacity,width] duration-300 will-change-transform`}
    >
      <View
        className={`h-[90px] flex justify-center items-center border-b border-gray-50 dark:border-gray-800/50`}
      >
        <View className="flex justify-between xxl:justify-center group-hover:justify-between items-center w-full px-6">
          <Link href="/" className="flex items-center gap-3">
            <View className="relative w-10 h-10">
              <Image
                width={40}
                height={40}
                src="/icons/icon-96x96.png"
                alt="logo"
                className="object-contain"
              />
            </View>
            <Text className="xxl:hidden group-hover:block text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-400">
              Intranet
            </Text>
          </Link>
          <Button
            onClick={() => setSidebar(false)}
            className="xxl:hidden text-gray-400"
          >
            <Icon
              name="bars"
              className="text-xl"
              aria-label={t("closeSidebar")}
            />
          </Button>
        </View>
      </View>

      <View className="overflow-y-auto h-[calc(100%-90px)] py-8 px-4">
        <View type="nav" className="flex flex-col gap-2">
          {data?.map((item: AsideProps["data"][number]) => {
            const isActive = path === String(item.value);

            return (
              <Link
                key={item.id}
                href={String(item.value)}
                target={item.target ?? "_self"}
                onClick={() => {
                  if (windowSize && windowSize < 1400) setSidebar(false);
                }}
                className={`
                  group/item flex items-center px-4 py-3 rounded-2xl transition-[background-color,color,transform] duration-200 relative overflow-hidden
                  xxl:justify-center group-hover:justify-start
                  ${isActive ? "active" : ""}
                `}
              >
                <View
                  className={`w-6 h-6 flex justify-center items-center text-lg z-10 shrink-0 ${isActive ? "active" : ""}`}
                >
                  <Icon name={`${item.icon}`} />
                </View>
                <Text
                  className={`ml-4 text-base tracking-wide font-medium whitespace-nowrap xxl:hidden group-hover:block ${isActive ? "font-semibold" : ""}`}
                >
                  {t(item.label || "")}
                </Text>
              </Link>
            );
          })}
        </View>
      </View>
    </View>
  );
};

export default SidebarHovered;
